/**
 * Error codes of the premium RAG exam flow.
 *
 * Two groups share the `rag_` prefix and must not be confused.
 *
 * BACKEND CODES (TF-773 Teil D), texts verbatim from
 * `core/backend/locales/t.*.json` with `%{x}` rewritten to `{{x}}`. Raised by
 * `core/backend/api/rag_exams.py` since TF-773 PR #249 and PR 2d, but only
 * registered in Teil D — and only half of the job was the registration:
 * `RAGService` (premium/frontend) threw a fixed legacy code without reading
 * the body, so no `error_code` ever reached `selectCode()`. Teil D moved
 * `triggerGeneration`, `retryGeneration`, `retrieveContext` and
 * `getTaskResult` onto `appErrorFromResponse`. What a component can now
 * receive:
 *
 *   rag_context_retrieval_failed  500  retrieve-context (context preview)
 *   rag_document_not_found        404  generate-exam, retrieve-context — a
 *                                      selected document deleted meanwhile
 *   rag_document_not_processed    400  generate-exam — a document preselected
 *                                      from the exam list that is not processed
 *   rag_generation_failed         500  generate-exam, retry-generation
 *   rag_no_institution            403  generate-exam, retry-generation, and
 *                                      available-documents via DocumentService
 *   rag_retry_already_succeeded   409  retry-generation — the job already saved
 *                                      its questions (TF-964); the panel still
 *                                      showed it as failed
 *   rag_retry_documents_unavailable
 *                                 400  retry-generation — a stored document is
 *                                      gone, unshared or unprocessed, or the job
 *                                      predates required document_ids (TF-969)
 *   rag_retry_owner_unavailable   400  retry-generation — a superuser retrying
 *                                      another user's task
 *   rag_tag_archived {{name}}     422  generate-exam — a tag archived after it
 *                                      was picked, or a pending tag whose
 *                                      creation returned an archived one
 *   rag_tag_ids_invalid           422  generate-exam — a picked tag deleted
 *   rag_task_not_found            404  retry-generation, task result (get-task-result)
 *                                      — job row gone
 *   rag_task_queue_unavailable    503  generate-exam, retry-generation
 *
 * TASK FAILURE CODES (TF-967). Not HTTP errors: a generation that ran and
 * failed. They arrive as `error_code` (+ `error_params`) on the WebSocket's
 * FAILURE/REVOKED frame (`core/backend/api/v1/websocket.py`) and on the
 * recovery response of GET /tasks/{task_id}/result, never through
 * `selectCode()`. `GenerationTasksContext` stores them on the task and
 * `errorMessageOf` (utils/generationTaskDisplay.ts) renders them in the order
 * of `translateError`, plus the backend's German `error` text as a middle
 * step — which `translateError` deliberately never renders. They must be
 * registered here: `errorMessageOf` only translates codes that pass
 * `isAppErrorCode()`, and `AppErrorCode.i18n.test.ts` then keeps all four
 * locales complete:
 *
 *   rag_task_failed                  any unmapped task exception (the common case)
 *   rag_task_no_context              too little searchable document material
 *   rag_task_pending_timeout {{seconds}}  WebSocket only — task never left PENDING
 *   rag_task_status_unavailable      WebSocket only — result backend unreachable
 *   rag_task_stream_error            WebSocket only — unexpected error in the stream
 *   rag_task_unknown_question_type   question type without a template
 *
 * `rag_generation_failed` above is a different failure: the 500 of the
 * synchronous start request, before any task exists.
 *
 * Deliberately NOT registered, because no call site can reach them:
 *
 *   rag_invalid_question_type    `validateRAGRequest` allows a strict subset of
 *                                the backend's types before anything is sent
 *   rag_retry_only_failed        the retry button exists only for FAILURE/
 *                                REVOKED tasks and is disabled while a retry
 *                                is in flight
 *   rag_retry_no_request_data    generate-exam always stores `request_data`
 *   rag_service_unhealthy        `RAGService.checkHealth` has no caller
 *   rag_task_dismiss_failed      `dismissTask` is fire-and-forget; the panel
 *                                hides the entry either way (TF-736)
 *
 * (`rag_get_documents_failed` predates both groups and lives in
 * `documents.ts`, because `DocumentService` is its only consumer.)
 *
 * OPERATION FALLBACKS (TF-996). One per `RAGService` method that needs a
 * fallback of its own, used when a non-ok response carries no registered
 * `error_code` (a proxy's 502, an unmigrated 500). A rejected `fetch` — a
 * network failure — is not caught in `RAGService` (except in
 * `previewContext`), so it reaches the caller as a plain error and the
 * caller's own fallback key renders. They were TF-671 dot-notation codes
 * (`rag.retryFailed` …) until TF-996 flattened them:
 *
 *   rag_active_tasks_failed     getActiveTasks, and dismissTask's never-shown
 *                               fallback
 *   rag_context_preview_failed  previewContext, for a non-AppError only
 *   rag_health_check_failed     checkHealth — no caller today, still thrown
 *   rag_not_available_in_core   GenerationTasksContext without the premium
 *                               RAGService (Core edition)
 *   rag_question_types_failed   getQuestionTypes
 *   rag_retry_failed            retryGeneration; also GenerationTasksBar's
 *                               fallback key
 *   rag_task_result_failed      getTaskResult
 *
 * Two former legacy codes were merged into the backend codes above instead:
 * `rag.examGenerationFailed` (triggerGeneration) into `rag_generation_failed`
 * and `rag.contextRetrievalFailed` (retrieveContext) into
 * `rag_context_retrieval_failed` — same endpoint, same failure, and the
 * backend already sends exactly that code for its 500. `retryGeneration`
 * keeps its own `rag_retry_failed`: "the retry failed" says more in the task
 * panel than the generic generation sentence would.
 *
 * FRONTEND-ONLY CODES (TF-772 PR 5): `rag_validation_*` come from
 * `RAGService.validateRAGRequest`, which checks the request in the browser
 * before anything is sent. No endpoint is involved and there is no backend
 * text to copy; the texts are the German literals the service used to
 * return. The `validation` infix keeps them apart from the backend group —
 * `rag_validation_invalid_question_types` reads like `rag_invalid_question_type`
 * but is a different sentence for a different failure.
 */
export const RAG_ERROR_CODES = [
  'rag_active_tasks_failed',
  'rag_context_preview_failed',
  'rag_context_retrieval_failed',
  'rag_document_not_found',
  'rag_document_not_processed',
  'rag_generation_failed',
  'rag_health_check_failed',
  'rag_no_institution',
  'rag_not_available_in_core',
  'rag_question_types_failed',
  'rag_retry_already_succeeded',
  'rag_retry_documents_unavailable',
  'rag_retry_failed',
  'rag_retry_owner_unavailable',
  'rag_tag_archived',
  'rag_tag_ids_invalid',
  'rag_task_failed',
  'rag_task_no_context',
  'rag_task_not_found',
  'rag_task_pending_timeout',
  'rag_task_queue_unavailable',
  'rag_task_result_failed',
  'rag_task_status_unavailable',
  'rag_task_stream_error',
  'rag_task_unknown_question_type',
  'rag_validation_document_required',
  'rag_validation_invalid_difficulty',
  'rag_validation_invalid_language',
  'rag_validation_invalid_question_types',
  'rag_validation_question_count_range',
  'rag_validation_question_type_required',
  'rag_validation_topic_too_short',
] as const;
